import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Stack } from '@mui/material';
import BillingReconciliationWorkspace from '../../components/billingReconciliation/BillingReconciliationWorkspace.jsx';
import DeleteIterationDialog from '../../components/billingReconciliation/DeleteIterationDialog.jsx';
import InheritanceToast from '../../components/billingReconciliation/InheritanceToast.jsx';
import IterationRoundsBar from '../../components/billingReconciliation/IterationRoundsBar.jsx';
import NewMonthDialog from '../../components/billingReconciliation/NewMonthDialog.jsx';
import NewRoundDialog from '../../components/billingReconciliation/NewRoundDialog.jsx';
import PageHeader from '../../components/billingReconciliation/PageHeader.jsx';
import ProgramMonthBar from '../../components/billingReconciliation/ProgramMonthBar.jsx';
import VarianceDrawer from '../../components/billingReconciliation/VarianceDrawer.jsx';
import { billingErrorMessage } from '../../components/billingReconciliation/errorMessage.js';
import { DEFAULT_ACTIVE_FILTERS } from '../../components/billingReconciliation/filters.js';
import { downloadCsv } from '../../components/billingReconciliation/exportCsv.js';
import {
  loadServiceMonthOptions,
  mergeServiceMonths,
  persistServiceMonthOption,
  serviceMonthsFromRuns,
} from '../../components/billingReconciliation/serviceMonths.js';
import {
  hasBillingReconciliationScopePreference,
  persistBillingReconciliationScopePreference,
  readBillingReconciliationScopePreference,
} from '../../components/billingReconciliation/scopePreference.js';
import { useIterationsState } from '../../hooks/billingReconciliation/useIterationsState.js';
import { useReviewsState } from '../../hooks/billingReconciliation/useReviewsState.js';
import { useReconciliationView } from '../../hooks/billingReconciliation/useReconciliationView.js';
import { isBillingReconciliationMockMode } from '../../hooks/api/billingReconciliationApi.js';
import { useBillingReconciliationRuns } from '../../hooks/api/useBillingReconciliationHooks.js';
import { contextWarmStatusKey } from '../../hooks/api/useBillingReconciliationEvidenceHooks.js';

/**
 * Billing Reconciliation — three-way compare between GM billing, DI CPQ, and
 * DPE enrollment state for a single service month. This file is intentionally
 * thin: orchestration stays here, while domain logic and presentation live in
 * the billingReconciliation components and hooks.
 */
export const isBillingRunProcessing = (iteration = {}) => {
  const explicitStatus = iteration.processingStatus || iteration.processing_status;
  if (explicitStatus) return explicitStatus === 'processing';
  return iteration.summary?.processing === true;
};

export const shouldRefetchAfterProcessingTransition = ({ mockMode, previous = {}, activeIteration = null } = {}) => (
  !mockMode
  && Boolean(activeIteration?.id)
  && previous.runId === activeIteration.id
  && previous.processing === true
  && !isBillingRunProcessing(activeIteration)
);

export const rowForVarianceDrawer = (row, program) => (
  row ? { ...row, program } : row
);

const idValue = (...values) => values.map((value) => String(value || '').trim()).find(Boolean) || '';

export const drawerContextFallbackRow = ({ selectedBac, drawerContext, serviceMonth } = {}) => {
  if (!selectedBac) return null;
  return {
    id: drawerContext?.varianceId || null,
    varianceId: drawerContext?.varianceId || null,
    runId: drawerContext?.roundRunId || null,
    roundRunId: drawerContext?.roundRunId || null,
    bac: selectedBac,
    dealerName: drawerContext?.dealerName || '',
    brand: drawerContext?.brand || '',
    status: 'match',
    gmTotal: 0,
    cpqTotal: 0,
    dpeTotal: 0,
    deltas: { gmVsCpq: 0, gmVsDpe: 0, cpqVsDpe: 0 },
    maxAbsDelta: 0,
    varianceDriver: drawerContext?.code
      ? { label: drawerContext.code, color: drawerContext.severity || 'info' }
      : null,
    sourceContext: true,
    gmLineItems: [],
    cpqLineItems: [],
    dpeLineItems: [],
    trailingWindow: [],
    serviceMonth,
    productCode: drawerContext?.productCode || '',
    productName: drawerContext?.productName || '',
  };
};

export const selectVarianceDrawerRow = ({
  reconciled = [],
  selectedBac = null,
  drawerContext = null,
  serviceMonth = null,
} = {}) => {
  const existing = reconciled.find((row) => row.bac === selectedBac);
  if (!selectedBac) return null;
  const contextVarianceId = idValue(drawerContext?.varianceId);
  const contextRunId = idValue(drawerContext?.roundRunId);
  const existingVarianceId = idValue(existing?.id, existing?.varianceId, existing?.variance_id);
  const existingRunId = idValue(existing?.runId, existing?.run_id, existing?.roundRunId, existing?.round_run_id);
  const contextTargetsDifferentRow = Boolean(
    existing
    && (
      (contextVarianceId && existingVarianceId && contextVarianceId !== existingVarianceId)
      || (contextRunId && existingRunId && contextRunId !== existingRunId)
    )
  );
  if (existing && !contextTargetsDifferentRow) return existing;
  return drawerContextFallbackRow({ selectedBac, drawerContext, serviceMonth });
};

export const cancelBillingScopeQueries = (queryClient) => (
  queryClient?.cancelQueries?.({ queryKey: ['billingRecon'] })
);

const BillingReconciliationPage = () => {
  const mockMode = isBillingReconciliationMockMode();
  const queryClient = useQueryClient();

  const [initialScope] = useState(() => ({
    ...readBillingReconciliationScopePreference(),
    hasStoredPreference: hasBillingReconciliationScopePreference(),
  }));
  const [program, setProgram] = useState(initialScope.program);
  const [serviceMonthOptions, setServiceMonthOptions] = useState(() => (
    mergeServiceMonths(loadServiceMonthOptions(), [initialScope.serviceMonth])
  ));
  const [serviceMonth, setServiceMonth] = useState(initialScope.serviceMonth);
  const userChangedScopeRef = useRef(false);
  const [threshold] = useState(1);
  const activeFilters = useMemo(() => new Set(DEFAULT_ACTIVE_FILTERS), []);
  const [search, setSearch] = useState('');

  const [mockSeedData, setMockSeedData] = useState(null);
  const [gm, setGm] = useState({ rows: [], fileName: null, file: null, seeded: false, serverParsed: false });
  const [cpq, setCpq] = useState({ rows: [], fileName: null, file: null, seeded: false, serverParsed: false });
  const [dpeDealers, setDpeDealers] = useState([]);
  const [cpqSourceMode, setCpqSourceMode] = useState('upload');
  const runMonthQuery = useBillingReconciliationRuns(
    { program, limit: 200 },
    { enabled: !mockMode },
  );
  const runServiceMonths = useMemo(
    () => serviceMonthsFromRuns(runMonthQuery.data),
    [runMonthQuery.data],
  );

  useEffect(() => {
    persistBillingReconciliationScopePreference({ program, serviceMonth });
  }, [program, serviceMonth]);

  useEffect(() => {
    if (mockMode || runServiceMonths.length === 0) return;
    setServiceMonthOptions((current) => mergeServiceMonths(current, runServiceMonths));
    const latestRunMonth = runServiceMonths[0]?.id;
    if (!latestRunMonth || initialScope.hasStoredPreference || userChangedScopeRef.current) return;
    setServiceMonth((current) => (
      String(latestRunMonth).localeCompare(String(current)) > 0 ? latestRunMonth : current
    ));
  }, [initialScope.hasStoredPreference, mockMode, runServiceMonths]);

  useEffect(() => {
    let cancelled = false;
    if (!mockMode) {
      setMockSeedData(null);
      setDpeDealers([]);
      if (gm.seeded) setGm({ rows: [], fileName: null, file: null, seeded: false, serverParsed: false });
      if (cpq.seeded) setCpq({ rows: [], fileName: null, file: null, seeded: false, serverParsed: false });
      return () => { cancelled = true; };
    }
    const modulePath = '../../components/billingReconciliation/mockData.js';
    import(/* @vite-ignore */ modulePath).then(({ seedCpqRows, seedDpe, seedGmRows }) => {
      if (cancelled) return;
      const seeds = { gmRows: seedGmRows, cpqRows: seedCpqRows, dpeDealers: seedDpe };
      setMockSeedData(seeds);
      setDpeDealers(seedDpe);
      setGm((current) => (current.file ? current : { rows: seedGmRows, fileName: null, file: null, seeded: true, serverParsed: false }));
      setCpq((current) => (current.file ? current : { rows: seedCpqRows, fileName: null, file: null, seeded: true, serverParsed: false }));
    });
    return () => { cancelled = true; };
  }, [mockMode, gm.seeded, cpq.seeded]);

  const iterationsState = useIterationsState({
    program,
    serviceMonth,
    gmRows: gm.rows,
    cpqRows: cpq.rows,
    dpeDealers,
    gmFile: gm.file,
    cpqFile: cpq.file,
    cpqSourceMode,
    mockSeedData,
  });

  const reviewsState = useReviewsState(iterationsState.activeIterationId);
  const clearReviewError = reviewsState.clearError;
  const activeIteration = useMemo(
    () => iterationsState.iterations.find((iteration) => iteration.id === iterationsState.activeIterationId) || null,
    [iterationsState.activeIterationId, iterationsState.iterations],
  );
  const previousReviewScopeRef = useRef(`${program}:${serviceMonth}:${iterationsState.activeIterationId || ''}`);

  useEffect(() => {
    const nextScope = `${program}:${serviceMonth}:${iterationsState.activeIterationId || ''}`;
    if (previousReviewScopeRef.current === nextScope) return;
    previousReviewScopeRef.current = nextScope;
    clearReviewError();
  }, [program, serviceMonth, iterationsState.activeIterationId, clearReviewError]);

  const {
    reconciled,
    summary,
    filtered,
    isLoading: varianceLoading,
    error: varianceError,
    refetch: refetchVariances,
    varianceListMeta,
  } = useReconciliationView({
    gmRows: gm.rows,
    cpqRows: cpq.rows,
    dpeDealers,
    serviceMonth,
    threshold,
    reviews: reviewsState.reviews,
    activeFilters,
    search,
    runId: iterationsState.activeIterationId,
    runSummary: activeIteration ? {
      ...(activeIteration.summary || {}),
      processingStatus: activeIteration.processingStatus || activeIteration.processing_status || null,
    } : null,
  });

  const [selectedBac, setSelectedBac] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContext, setDrawerContext] = useState(null);
  const [drawerNavigation, setDrawerNavigation] = useState(null);
  const [newMonthOpen, setNewMonthOpen] = useState(false);
  const [newRoundOpen, setNewRoundOpen] = useState(false);
  const previousProcessingRef = useRef({ runId: null, processing: false });

  useEffect(() => {
    if (mockMode || !activeIteration?.id) return undefined;
    const processing = isBillingRunProcessing(activeIteration);
    const previous = previousProcessingRef.current;
    previousProcessingRef.current = { runId: activeIteration.id, processing };
    if (!shouldRefetchAfterProcessingTransition({ mockMode, previous, activeIteration })) return undefined;
    const timer = window.setTimeout(() => {
      refetchVariances?.();
      queryClient.invalidateQueries({ queryKey: contextWarmStatusKey({ runId: activeIteration.id, contextMode: 'critical' }) });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [activeIteration, activeIteration?.id, activeIteration?.processingStatus, activeIteration?.processing_status, activeIteration?.summary?.processing, mockMode, queryClient, refetchVariances]);

  const selectedRow = useMemo(
    () => selectVarianceDrawerRow({ reconciled, selectedBac, drawerContext, serviceMonth }),
    [drawerContext, reconciled, selectedBac, serviceMonth],
  );

  useEffect(() => {
    if (!drawerOpen) {
      setSelectedBac(null);
      setDrawerContext(null);
    }
  }, [drawerOpen]);

  const openDrawer = (bac, context = null) => {
    setSelectedBac(bac);
    setDrawerContext(context);
    setDrawerOpen(true);
  };
  const handleProgramChange = useCallback((nextProgram) => {
    userChangedScopeRef.current = true;
    cancelBillingScopeQueries(queryClient);
    setProgram(nextProgram);
  }, [queryClient]);
  const handleServiceMonthChange = useCallback((nextServiceMonth) => {
    userChangedScopeRef.current = true;
    cancelBillingScopeQueries(queryClient);
    setServiceMonth(nextServiceMonth);
  }, [queryClient]);

  const roundsForMonth = useMemo(
    () => iterationsState.iterations.filter((iteration) => iteration.monthId === serviceMonth || iteration.serviceMonth === serviceMonth),
    [iterationsState.iterations, serviceMonth],
  );
  const nextRoundNumber = useMemo(
    () => Math.max(0, ...roundsForMonth.map((iteration) => Number(iteration.number || iteration.roundNumber || 0))) + 1,
    [roundsForMonth],
  );
  const handleCreateRound = ({ gm: nextGm, cpq: nextCpq, cpqSourceMode: nextCpqSourceMode }) => {
    setGm(nextGm);
    setCpq(nextCpq);
    setCpqSourceMode(nextCpqSourceMode);
    iterationsState.createIteration({
      gmFile: nextGm.file,
      cpqFile: nextCpqSourceMode === 'latest_gm_export' ? null : nextCpq.file,
      cpqSourceMode: nextCpqSourceMode,
      gmRows: nextGm.rows,
      cpqRows: nextCpq.rows,
    });
    setNewRoundOpen(false);
  };

  const handleCreateMonth = useCallback((monthId) => {
    const nextOptions = persistServiceMonthOption(monthId);
    setServiceMonthOptions(nextOptions);
    setServiceMonth(monthId);
    setNewMonthOpen(false);
  }, []);

  const handleBackfillImported = useCallback((run) => {
    const importedMonth = run?.serviceMonth || run?.service_month || run?.monthId;
    if (importedMonth) handleCreateMonth(importedMonth);
    iterationsState.registerImportedRun(run);
  }, [handleCreateMonth, iterationsState]);

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: (t) =>
          t.palette.mode === 'dark'
            ? 'radial-gradient(ellipse at top, rgba(25, 118, 210, 0.08), transparent 60%), linear-gradient(180deg, rgba(0,0,0,0.1) 0%, transparent 100%)'
            : 'radial-gradient(ellipse at top, rgba(25, 118, 210, 0.05), transparent 60%), linear-gradient(180deg, rgba(0,0,0,0.015) 0%, transparent 100%)',
        mx: -2,
        px: 2,
        pt: { xs: 2, sm: 4 },
        pb: { xs: 2, sm: 5 },
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Stack spacing={2.5}>
          <PageHeader />

          <ProgramMonthBar
            program={program}
            onProgramChange={handleProgramChange}
            serviceMonth={serviceMonth}
            onServiceMonthChange={handleServiceMonthChange}
            serviceMonths={serviceMonthOptions}
            onCreateMonth={() => setNewMonthOpen(true)}
          />

          <IterationRoundsBar
            monthId={serviceMonth}
            iterations={iterationsState.iterations}
            activeIterationId={iterationsState.activeIterationId}
            onSelect={(nextIterationId) => {
              cancelBillingScopeQueries(queryClient);
              iterationsState.setActiveIterationId(nextIterationId);
            }}
            onCreate={() => setNewRoundOpen(true)}
            onRequestDelete={iterationsState.requestDeleteIteration}
            createDisabled={iterationsState.isCreating}
            gmRows={gm.rows}
            cpqRows={cpq.rows}
            dpeDealers={dpeDealers}
          />

          <InheritanceToast
            toast={iterationsState.inheritanceToast}
            onClose={iterationsState.dismissInheritanceToast}
          />

          {iterationsState.error && (
            <Alert severity="error">
              {billingErrorMessage(iterationsState.error, 'Billing reconciliation round could not be loaded or created.')}
            </Alert>
          )}

          {reviewsState.error && (
            <Alert severity="error" onClose={reviewsState.clearError}>
              {billingErrorMessage(reviewsState.error, 'Review changes could not be saved. Refresh before relying on this review state.')}
            </Alert>
          )}

          <BillingReconciliationWorkspace
            drawerOpen={drawerOpen}
            filtered={filtered}
            onExport={(rows) => downloadCsv(rows)}
            onOpenBac={openDrawer}
            onRowClick={openDrawer}
            onSearchChange={setSearch}
            program={program}
            reconciled={reconciled}
            runId={iterationsState.activeIterationId}
            iterations={iterationsState.iterations}
            search={search}
            selectedBac={selectedBac}
            serviceMonth={serviceMonth}
            summary={summary}
            threshold={threshold}
            varianceError={varianceError}
            varianceLoading={varianceLoading}
            varianceListMeta={varianceListMeta}
            onRetryVariances={refetchVariances}
            onDrawerNavigationChange={setDrawerNavigation}
            onBackfillImported={handleBackfillImported}
            onReviewsReset={reviewsState.resetReviewFlags}
          />
        </Stack>

        <NewRoundDialog
          open={newRoundOpen}
          nextRoundNumber={nextRoundNumber}
          program={program}
          initialSourceMode={cpqSourceMode}
          mockMode={mockMode}
          mockSeedData={mockSeedData}
          creating={iterationsState.isCreating}
          onClose={() => setNewRoundOpen(false)}
          onCreate={handleCreateRound}
        />

        <NewMonthDialog
          open={newMonthOpen}
          initialMonth={serviceMonth}
          existingMonths={serviceMonthOptions}
          onClose={() => setNewMonthOpen(false)}
          onCreate={handleCreateMonth}
        />

        <VarianceDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          row={rowForVarianceDrawer(selectedRow, program)}
          review={selectedRow?.review}
          onReviewChange={reviewsState.handleReviewChange}
          saveReview={reviewsState.saveReviewScoped}
          saveReviewAsync={reviewsState.saveReviewScopedAsync}
          setNotes={reviewsState.setNotesScoped}
          runId={drawerContext?.roundRunId || iterationsState.activeIterationId}
          serviceMonth={serviceMonth}
          importMode={activeIteration?.importMode || activeIteration?.import_mode || null}
          mockMode={mockMode}
          context={drawerContext}
          navigation={drawerNavigation}
        />

        <DeleteIterationDialog
          pending={iterationsState.pendingDelete}
          onCancel={iterationsState.cancelDeleteIteration}
          onConfirm={iterationsState.confirmDeleteIteration}
        />
      </Box>
    </Box>
  );
};

export default BillingReconciliationPage;
